import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const TEST_BOT_URL = process.env.REACT_APP_TEST_BOT_URL || 'https://test-bot.remote-assistant.io';
const TEST_BOT_QUEUE_URL = process.env.REACT_APP_TEST_BOT_QUEUE_URL || '';

// Default test targets
const TARGETS = [
  {
    label: 'Dev Barber (+12603515782)',
    phone: '+12603515782',
    prefix: 'dev_85095a1c-f414-4c53-a529-0ac594bee192_',
    wsHost: 'bot-85095a1c-f414-4c53-a529-0ac594bee192.remote-assistant.io',
    simKey: 'sim-85095a1c-f414-4c',
    businessId: 'ab361cb5-0678-44e0-b2bd-9ef57cd63b1f',
    callerPhone: '+16043736189',
  },
];

const SCENARIOS = [
  'barber', 'walkin', 'escalate', 'services', 'faq', 'color',
  'specific_date', 'staff_preference', 'cancel', 'pricing',
  'faq_consult', 'list_services', 'lost_wallet', 'meeting',
];


function LatencyChart({ data, target = 800 }) {
  if (!data || !data.length) return null;

  const maxVal = Math.max(...data.map(d => d.perceived_ms), target * 1.5);
  const width = 600;
  const height = 200;
  const padding = { top: 20, right: 20, bottom: 30, left: 50 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const barWidth = Math.min(40, (chartW / data.length) - 4);

  return (
    <svg width={width} height={height} style={{ maxWidth: '100%' }}>
      {/* Target line */}
      <line
        x1={padding.left} x2={width - padding.right}
        y1={padding.top + chartH * (1 - target / maxVal)}
        y2={padding.top + chartH * (1 - target / maxVal)}
        stroke="#ef4444" strokeWidth="1" strokeDasharray="4,4"
      />
      <text
        x={width - padding.right + 2}
        y={padding.top + chartH * (1 - target / maxVal) + 4}
        fontSize="10" fill="#ef4444"
      >{target}ms</text>

      {/* Bars */}
      {data.map((d, i) => {
        const x = padding.left + (i * (chartW / data.length)) + (chartW / data.length - barWidth) / 2;
        const barH = (d.perceived_ms / maxVal) * chartH;
        const y = padding.top + chartH - barH;
        const color = d.perceived_ms < target ? '#22c55e' : d.perceived_ms < 1500 ? '#f59e0b' : '#ef4444';

        return (
          <g key={i}>
            <rect x={x} y={y} width={barWidth} height={barH} fill={color} rx="2" />
            <text x={x + barWidth / 2} y={y - 4} textAnchor="middle" fontSize="10" fill="#666">
              {d.perceived_ms}
            </text>
            <text x={x + barWidth / 2} y={height - 8} textAnchor="middle" fontSize="9" fill="#999">
              {i + 1}
            </text>
          </g>
        );
      })}

      {/* Y-axis label */}
      <text x={8} y={height / 2} textAnchor="middle" fontSize="10" fill="#999" transform={`rotate(-90, 8, ${height / 2})`}>
        ms
      </text>
      {/* X-axis label */}
      <text x={width / 2} y={height - 1} textAnchor="middle" fontSize="10" fill="#999">
        Turn
      </text>
    </svg>
  );
}

function StatusBadge({ status }) {
  const colors = {
    ringing: { bg: '#fef3c7', text: '#92400e' },
    connected: { bg: '#dbeafe', text: '#1e40af' },
    completed: { bg: '#d1fae5', text: '#065f46' },
    validated: { bg: '#d1fae5', text: '#065f46' },
    validating: { bg: '#ede9fe', text: '#5b21b6' },
    failed: { bg: '#fee2e2', text: '#991b1b' },
  };
  const c = colors[status] || { bg: '#f3f4f6', text: '#374151' };
  return (
    <span style={{ backgroundColor: c.bg, color: c.text, padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 500 }}>
      {status}
    </span>
  );
}

function TestRunner() {
  const { signOut } = useAuth();
  const [activeTab, setActiveTab] = useState('run');      // run | history | detail
  const [mode, setMode] = useState('text');               // text | voice
  const [target, setTarget] = useState(TARGETS[0]);
  const [scenario, setScenario] = useState('barber');
  const [autoValidate, setAutoValidate] = useState(false);
  const [liveSession, setLiveSession] = useState(null);
  const [polling, setPolling] = useState(false);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyDate, setHistoryDate] = useState(new Date().toISOString().split('T')[0]);
  const [detailResult, setDetailResult] = useState(null);
  const [error, setError] = useState('');

  // Stop a live call/simulation
  const stopCall = async () => {
    if (liveSession?.session_id) {
      try {
        await fetch(`${TEST_BOT_URL}/test/hangup/${liveSession.session_id}`, { method: 'POST' });
      } catch (e) { /* ignore */ }
    }
    setPolling(false);
    setRunning(false);
    setLiveSession(prev => prev ? { ...prev, status: 'completed' } : prev);
  };

  // Submit a test job through the queue API or direct test-bot
  const _submitJob = async (endpoint, payload) => {
    if (TEST_BOT_QUEUE_URL) {
      const resp = await fetch(`${TEST_BOT_QUEUE_URL}/queue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint, payload }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to queue job');
      return { ...data, session_id: data.job_id, queued: true };
    }
    const resp = await fetch(`${TEST_BOT_URL}/test/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.detail || `Failed to start ${endpoint}`);
    return data;
  };

  // Start text simulation via test bot API (runs original simulator in-process)
  const startTextSim = async () => {
    setError('');
    try {
      const data = await _submitJob('simulate', {
        target: target.phone,
        scenario,
        ws_host: target.wsHost,
        sim_key: target.simKey,
        caller_phone: target.callerPhone,
        table_prefix: target.prefix,
      });
      setLiveSession({ ...data, mode: 'simulation' });
      setPolling(true);
      setRunning(true);
    } catch (e) {
      setError(e.message);
    }
  };

  // Start a PSTN voice call via test bot API
  const startVoiceCall = async () => {
    setError('');
    try {
      const resp = await fetch(`${TEST_BOT_URL}/test/call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target: target.phone,
          scenario,
          table_prefix: target.prefix,
          auto_validate: autoValidate,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed to start call');
      setLiveSession({ ...data, mode: 'voice' });
      setPolling(true);
      setRunning(true);
    } catch (e) {
      setError(e.message);
    }
  };

  // Start test (routes to text or voice)
  const startCall = () => {
    if (mode === 'text') {
      startTextSim();
    } else {
      startVoiceCall();
    }
  };

  // Poll live session status
  useEffect(() => {
    if (!polling || !liveSession?.session_id) return;

    const interval = setInterval(async () => {
      try {
        const resp = await fetch(`${TEST_BOT_URL}/test/results/${liveSession.session_id}`);
        if (resp.ok) {
          const data = await resp.json();
          setLiveSession(prev => ({ ...prev, ...data }));
          if (data.status === 'completed' || data.status === 'validated') {
            setPolling(false);
          }
        }
      } catch (e) {
        // Session might not be in S3 yet, keep polling
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [polling, liveSession?.session_id]);

  // Load history
  const loadHistory = useCallback(async () => {
    try {
      const resp = await fetch(`${TEST_BOT_URL}/test/results?date=${historyDate}`);
      if (resp.ok) {
        const data = await resp.json();
        setHistory(data.results || []);
      }
    } catch (e) {
      setError(e.message);
    }
  }, [historyDate]);

  useEffect(() => {
    if (activeTab === 'history') loadHistory();
  }, [activeTab, historyDate, loadHistory]);

  // Load detail
  const loadDetail = async (sessionId) => {
    try {
      const resp = await fetch(`${TEST_BOT_URL}/test/results/${sessionId}`);
      if (resp.ok) {
        const data = await resp.json();
        setDetailResult(data);
        setActiveTab('detail');
      }
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9f9fb' }}>
      {/* Nav */}
      <nav className="bg-white shadow-sm" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link to="/dashboard">
                <img src="/assets/logo---dark-with-color.png" alt="Remote Assistant" className="h-8 w-auto" />
              </Link>
              <h1 className="text-xl font-montserrat font-semibold" style={{ color: '#222324' }}>
                E2E Test Runner
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/dashboard" className="font-inter text-sm font-medium px-3 py-2" style={{ color: '#979ba4' }}>
                Dashboard
              </Link>
              <button onClick={signOut} className="font-inter text-sm font-medium px-3 py-2" style={{ color: '#979ba4' }}>
                Sign out
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-white rounded-lg p-1 shadow-sm" style={{ border: '1px solid #e5e7eb', display: 'inline-flex' }}>
          {[['run', 'Run Test'], ['history', 'History'], ['detail', 'Detail']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className="px-4 py-2 rounded-md text-sm font-medium transition-colors"
              style={{
                backgroundColor: activeTab === key ? '#055efd' : 'transparent',
                color: activeTab === key ? '#fff' : '#666',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>
            {error}
            <button onClick={() => setError('')} className="ml-2 font-bold">x</button>
          </div>
        )}

        {/* === RUN TAB === */}
        {activeTab === 'run' && (
          <div className="space-y-6">
            {/* Controls */}
            <div className="bg-white rounded-2xl shadow-sm p-6" style={{ border: '1px solid #e5e7eb' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#222324' }}>Launch Test</h3>

              {/* Mode toggle */}
              <div className="flex space-x-1 mb-4 bg-gray-100 rounded-lg p-1" style={{ display: 'inline-flex' }}>
                {[['text', 'Simulation'], ['voice', 'Voice (PSTN)']].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setMode(key)}
                    className="px-4 py-1.5 rounded-md text-sm font-medium transition-colors"
                    style={{
                      backgroundColor: mode === key ? '#fff' : 'transparent',
                      color: mode === key ? '#222324' : '#999',
                      boxShadow: mode === key ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#666' }}>Target Assistant</label>
                  <select
                    value={target.phone}
                    onChange={e => setTarget(TARGETS.find(t => t.phone === e.target.value) || TARGETS[0])}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    style={{ borderColor: '#d1d5db' }}
                  >
                    {TARGETS.map(t => (
                      <option key={t.phone} value={t.phone}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#666' }}>Scenario</label>
                  <select
                    value={scenario}
                    onChange={e => setScenario(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    style={{ borderColor: '#d1d5db' }}
                  >
                    {SCENARIOS.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={startCall}
                    disabled={running}
                    className="w-full px-4 py-2 rounded-lg text-white font-medium text-sm transition-colors"
                    style={{ backgroundColor: running ? '#9ca3af' : '#055efd' }}
                  >
                    {running ? 'Running...' : mode === 'text' ? 'Run Simulation' : 'Start PSTN Call'}
                  </button>
                </div>
              </div>
              {mode === 'voice' && (
                <div className="mt-3">
                  <label className="flex items-center space-x-2 text-sm" style={{ color: '#666' }}>
                    <input type="checkbox" checked={autoValidate} onChange={e => setAutoValidate(e.target.checked)} />
                    <span>Auto-validate checkpoints after call</span>
                  </label>
                </div>
              )}
              {mode === 'text' && (
                <p className="mt-3 text-xs" style={{ color: '#999' }}>
                  Runs the full assistant pipeline locally in the test bot container. Same simulator used for production validation. No PSTN call or Twilio charges.
                </p>
              )}
            </div>

            {/* Live Session */}
            {liveSession && (
              <div className="bg-white rounded-2xl shadow-sm p-6" style={{ border: '1px solid #e5e7eb' }}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold" style={{ color: '#222324' }}>
                    Live: {liveSession.session_id}
                  </h3>
                  <div className="flex items-center space-x-3">
                    <StatusBadge status={liveSession.status} />
                    {liveSession.duration_s && (
                      <span className="text-sm" style={{ color: '#999' }}>{liveSession.duration_s}s</span>
                    )}
                    {polling && (
                      <button
                        onClick={stopCall}
                        className="px-3 py-1 rounded-lg text-sm font-medium text-white"
                        style={{ backgroundColor: '#ef4444' }}
                      >
                        Stop Call
                      </button>
                    )}
                  </div>
                </div>

                {/* Latency Summary */}
                {liveSession.latency?.data?.length > 0 && (
                  <div className="mb-4">
                    <div className="grid grid-cols-5 gap-3 mb-3">
                      {[
                        ['Avg', `${liveSession.latency.avg_ms}ms`],
                        ['P50', `${liveSession.latency.p50_ms}ms`],
                        ['Min', `${liveSession.latency.min_ms}ms`],
                        ['Max', `${liveSession.latency.max_ms}ms`],
                        ['< 800ms', `${liveSession.latency.under_800ms}/${liveSession.latency.turns}`],
                      ].map(([label, value]) => (
                        <div key={label} className="text-center p-2 rounded-lg" style={{ backgroundColor: '#f9fafb' }}>
                          <div className="text-xs" style={{ color: '#999' }}>{label}</div>
                          <div className="text-sm font-semibold" style={{ color: '#222324' }}>{value}</div>
                        </div>
                      ))}
                    </div>
                    <LatencyChart data={liveSession.latency.data} />
                  </div>
                )}

                {/* Conversation */}
                {liveSession.conversation?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2" style={{ color: '#666' }}>
                      Conversation ({liveSession.conversation.length} turns)
                    </h4>
                    <div className="space-y-1 max-h-64 overflow-y-auto" style={{ fontSize: '13px' }}>
                      {liveSession.conversation.map((turn, i) => (
                        <div key={i} className="flex space-x-2">
                          <span className="flex-shrink-0 font-mono text-xs px-1 rounded"
                            style={{
                              backgroundColor: turn.role === 'user' ? '#dbeafe' : '#d1fae5',
                              color: turn.role === 'user' ? '#1e40af' : '#065f46',
                              minWidth: '70px', textAlign: 'center', lineHeight: '20px',
                            }}>
                            {turn.role === 'user' ? 'CALLER' : 'ASST'}
                          </span>
                          <span style={{ color: '#374151' }}>{turn.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {liveSession.status === 'completed' || liveSession.status === 'validated' ? (
                  <button
                    onClick={() => { loadDetail(liveSession.session_id); }}
                    className="mt-4 px-4 py-2 rounded-lg text-sm font-medium"
                    style={{ backgroundColor: '#f3f4f6', color: '#374151' }}
                  >
                    View Full Report
                  </button>
                ) : null}
              </div>
            )}
          </div>
        )}

        {/* === HISTORY TAB === */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-2xl shadow-sm p-6" style={{ border: '1px solid #e5e7eb' }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold" style={{ color: '#222324' }}>Test History</h3>
              <input
                type="date"
                value={historyDate}
                onChange={e => setHistoryDate(e.target.value)}
                className="border rounded-lg px-3 py-1 text-sm"
                style={{ borderColor: '#d1d5db' }}
              />
            </div>

            {history.length === 0 ? (
              <p className="text-sm" style={{ color: '#999' }}>No tests found for {historyDate}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <th className="text-left py-2 px-2 font-medium" style={{ color: '#666' }}>Session</th>
                      <th className="text-left py-2 px-2 font-medium" style={{ color: '#666' }}>Scenario</th>
                      <th className="text-left py-2 px-2 font-medium" style={{ color: '#666' }}>Status</th>
                      <th className="text-left py-2 px-2 font-medium" style={{ color: '#666' }}>Checkpoints</th>
                      <th className="text-left py-2 px-2 font-medium" style={{ color: '#666' }}>Time</th>
                      <th className="text-right py-2 px-2 font-medium" style={{ color: '#666' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(r => (
                      <tr key={r.session_id} style={{ borderBottom: '1px solid #f3f4f6' }}
                        className="hover:bg-gray-50 cursor-pointer" onClick={() => loadDetail(r.session_id)}>
                        <td className="py-2 px-2 font-mono text-xs">{r.session_id}</td>
                        <td className="py-2 px-2">{r.scenario}</td>
                        <td className="py-2 px-2"><StatusBadge status={r.status} /></td>
                        <td className="py-2 px-2">{r.checkpoint_summary || '-'}</td>
                        <td className="py-2 px-2 text-xs" style={{ color: '#999' }}>
                          {r.last_modified ? new Date(r.last_modified).toLocaleTimeString() : '-'}
                        </td>
                        <td className="py-2 px-2 text-right">
                          <span style={{ color: '#055efd', fontSize: '12px' }}>View</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* === DETAIL TAB === */}
        {activeTab === 'detail' && detailResult && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-sm p-6" style={{ border: '1px solid #e5e7eb' }}>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: '#222324' }}>
                    {detailResult.scenario} — {detailResult.session_id}
                  </h3>
                  <p className="text-sm" style={{ color: '#999' }}>
                    Target: {detailResult.target} | Duration: {detailResult.duration_s}s |
                    Turns: {detailResult.conversation?.length || 0}
                  </p>
                </div>
                <StatusBadge status={detailResult.status} />
              </div>
            </div>

            {/* Latency Chart */}
            {detailResult.latency?.data?.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-6" style={{ border: '1px solid #e5e7eb' }}>
                <h4 className="text-md font-semibold mb-3" style={{ color: '#222324' }}>
                  Perceived Response Latency
                </h4>
                <div className="grid grid-cols-5 gap-3 mb-4">
                  {[
                    ['Avg', `${detailResult.latency.avg_ms}ms`],
                    ['P50', `${detailResult.latency.p50_ms}ms`],
                    ['P90', `${detailResult.latency.p90_ms}ms`],
                    ['Min', `${detailResult.latency.min_ms}ms`],
                    ['Max', `${detailResult.latency.max_ms}ms`],
                  ].map(([label, value]) => (
                    <div key={label} className="text-center p-3 rounded-lg" style={{ backgroundColor: '#f9fafb' }}>
                      <div className="text-xs" style={{ color: '#999' }}>{label}</div>
                      <div className="text-lg font-semibold" style={{ color: '#222324' }}>{value}</div>
                    </div>
                  ))}
                </div>
                <LatencyChart data={detailResult.latency.data} />

                {/* Per-turn table */}
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <th className="text-left py-1 px-2" style={{ color: '#666' }}>#</th>
                        <th className="text-right py-1 px-2" style={{ color: '#666' }}>Gap</th>
                        <th className="text-left py-1 px-2" style={{ color: '#666' }}>Caller said</th>
                        <th className="text-left py-1 px-2" style={{ color: '#666' }}>Assistant said</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailResult.latency.data.map(d => (
                        <tr key={d.turn} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td className="py-1 px-2">{d.turn}</td>
                          <td className="py-1 px-2 text-right font-mono" style={{
                            color: d.perceived_ms < 800 ? '#16a34a' : d.perceived_ms < 1500 ? '#d97706' : '#dc2626'
                          }}>
                            {d.perceived_ms}ms
                          </td>
                          <td className="py-1 px-2" style={{ color: '#374151', maxWidth: '250px' }}>
                            {d.user_text || '-'}
                          </td>
                          <td className="py-1 px-2" style={{ color: '#374151', maxWidth: '250px' }}>
                            {d.assistant_text || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Checkpoints */}
            {detailResult.checkpoints?.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-6" style={{ border: '1px solid #e5e7eb' }}>
                <h4 className="text-md font-semibold mb-3" style={{ color: '#222324' }}>
                  Checkpoints — {detailResult.checkpoint_summary}
                </h4>
                <div className="space-y-2">
                  {detailResult.checkpoints.map((cp, i) => (
                    <div key={i} className="flex items-start space-x-2 text-sm">
                      <span className="flex-shrink-0 font-mono text-xs px-2 py-0.5 rounded"
                        style={{
                          backgroundColor: cp.status === 'PASS' ? '#d1fae5' : cp.status === 'FAIL' ? '#fee2e2' : '#fef3c7',
                          color: cp.status === 'PASS' ? '#065f46' : cp.status === 'FAIL' ? '#991b1b' : '#92400e',
                        }}>
                        {cp.status}
                      </span>
                      <div>
                        <span className="font-medium">{cp.name}</span>
                        {cp.details && <span style={{ color: '#666' }}> — {cp.details}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Full Conversation */}
            {detailResult.conversation?.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-6" style={{ border: '1px solid #e5e7eb' }}>
                <h4 className="text-md font-semibold mb-3" style={{ color: '#222324' }}>
                  Conversation ({detailResult.conversation.length} turns)
                </h4>
                <div className="space-y-2">
                  {detailResult.conversation.map((turn, i) => (
                    <div key={i} className="flex space-x-3 text-sm">
                      <span className="flex-shrink-0 font-mono text-xs px-2 py-0.5 rounded"
                        style={{
                          backgroundColor: turn.role === 'user' ? '#dbeafe' : '#d1fae5',
                          color: turn.role === 'user' ? '#1e40af' : '#065f46',
                          minWidth: '60px', textAlign: 'center',
                        }}>
                        {turn.role === 'user' ? 'CALLER' : 'ASST'}
                      </span>
                      <span style={{ color: '#374151' }}>{turn.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Assistant-side Conversation */}
            {detailResult.assistant_conversation?.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-6" style={{ border: '1px solid #e5e7eb' }}>
                <h4 className="text-md font-semibold mb-3" style={{ color: '#222324' }}>
                  Assistant-Side Conversation ({detailResult.assistant_conversation.length} turns)
                </h4>
                <div className="space-y-2">
                  {detailResult.assistant_conversation.map((turn, i) => (
                    <div key={i} className="flex space-x-3 text-sm">
                      <span className="flex-shrink-0 font-mono text-xs px-2 py-0.5 rounded"
                        style={{
                          backgroundColor: turn.direction === 'user' ? '#dbeafe' : turn.direction === 'assistant' ? '#d1fae5' : '#fef3c7',
                          color: turn.direction === 'user' ? '#1e40af' : turn.direction === 'assistant' ? '#065f46' : '#92400e',
                          minWidth: '60px', textAlign: 'center',
                        }}>
                        {turn.direction === 'user' ? 'USER' : turn.direction === 'assistant' ? 'ASST' : 'SYS'}
                      </span>
                      <span style={{ color: '#374151' }}>{turn.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default TestRunner;
